import { ref, computed, watch } from 'vue';

// Shared state for all components
const menuState = ref({
  isMenuOpen: false,
  isMenuCollapsed: false,
  isMenuHidden: true,
});

// Registry of open dropdown/submenu items (keyed by unique item ID)
// This is the single source of truth for which items are expanded
const openItems = ref(new Set());

// Snapshot of open items before menu collapse (for restore on expand)
const collapsedOpenItemsSnapshot = ref(new Set());

// Counter for generating unique IDs
let idCounter = 0;

/**
 * Generate a unique ID for menu items that don't have an explicit one.
 */
export function generateMenuItemId() {
  // eslint-disable-next-line no-plusplus
  return `__menu_item_${++idCounter}`;
}

// Update body classes based on menu state
const updateBodyClasses = () => {
  const $body = document.body;
  const isMobile = window.innerWidth < 1200;

  $body.classList.remove('menu-open', 'menu-collapsed', 'menu-hide', 'menu-expanded', 'vertical-overlay-menu');

  // In mobile, use vertical-overlay-menu instead of vertical-menu-modern for proper menu positioning
  // The vertical-menu-modern class sets width:0 in mobile which prevents the menu from showing
  if (isMobile) {
    $body.classList.remove('vertical-menu-modern');
    $body.classList.add('vertical-overlay-menu');
    if (menuState.value.isMenuOpen) {
      $body.classList.add('menu-open');
    } else {
      $body.classList.add('menu-hide');
    }
  } else {
    // Restore vertical-menu-modern for desktop
    $body.classList.add('vertical-menu-modern');
    if (menuState.value.isMenuCollapsed) {
      $body.classList.add('menu-collapsed');
    } else if (menuState.value.isMenuHidden) {
      $body.classList.add('menu-hide');
    } else {
      $body.classList.add('menu-expanded');
    }
  }
};

// Expose toggleMenu to window for direct access
if (typeof window !== 'undefined') {
  window.toggleMenuGlobal = () => {
    const isMobile = window.innerWidth < 1200;
    if (isMobile) {
      menuState.value.isMenuHidden = !menuState.value.isMenuHidden;
      menuState.value.isMenuOpen = !menuState.value.isMenuHidden;
      menuState.value.isMenuCollapsed = false;
    } else {
      menuState.value.isMenuCollapsed = !menuState.value.isMenuCollapsed;
      menuState.value.isMenuOpen = false;
      menuState.value.isMenuHidden = false;
    }
    updateBodyClasses();
  };
}

export function useMenuState() {
  const isMenuOpen = computed(() => menuState.value.isMenuOpen);
  const isMenuCollapsed = computed(() => menuState.value.isMenuCollapsed);
  const isMenuHidden = computed(() => menuState.value.isMenuHidden);

  const toggleMenu = () => {
    window.toggleMenuGlobal?.();
  };

  const openMenu = () => {
    menuState.value.isMenuOpen = true;
    menuState.value.isMenuHidden = false;
    menuState.value.isMenuCollapsed = false;
    updateBodyClasses();
  };

  const hideMenu = () => {
    menuState.value.isMenuHidden = true;
    menuState.value.isMenuOpen = false;
    menuState.value.isMenuCollapsed = false;
    updateBodyClasses();
  };

  const expandMenu = () => {
    menuState.value.isMenuOpen = false;
    menuState.value.isMenuHidden = false;
    menuState.value.isMenuCollapsed = false;
    updateBodyClasses();
  };

  const collapseMenu = () => {
    menuState.value.isMenuOpen = false;
    menuState.value.isMenuHidden = false;
    menuState.value.isMenuCollapsed = true;
    updateBodyClasses();
  };

  // ── Item-level open state management ──

  /**
   * Check if a menu item is currently open.
   * @param {string} itemId
   * @returns {boolean}
   */
  const isItemOpen = (itemId) => {
    return openItems.value.has(itemId);
  };

  /**
   * Toggle a menu item's open state.
   * @param {string} itemId
   */
  const toggleItem = (itemId) => {
    const newSet = new Set(openItems.value);
    if (newSet.has(itemId)) {
      newSet.delete(itemId);
    } else {
      newSet.add(itemId);
    }
    openItems.value = newSet;
  };

  /**
   * Set a menu item's open state explicitly.
   * @param {string} itemId
   * @param {boolean} isOpen
   */
  const setItemOpen = (itemId, isOpen) => {
    const newSet = new Set(openItems.value);
    if (isOpen) {
      newSet.add(itemId);
    } else {
      newSet.delete(itemId);
    }
    openItems.value = newSet;
  };

  /**
   * Called by MenuLeftLayout when the menu is about to collapse (hover leave).
   * Saves a snapshot of currently open items and closes all of them.
   */
  const snapshotAndCloseAll = () => {
    collapsedOpenItemsSnapshot.value = new Set(openItems.value);
    openItems.value = new Set();
  };

  /**
   * Called by MenuLeftLayout when the menu is expanded again (hover enter).
   * Restores items that were open before collapse.
   */
  const restoreFromSnapshot = () => {
    openItems.value = new Set(collapsedOpenItemsSnapshot.value);
    collapsedOpenItemsSnapshot.value = new Set();
  };

  /**
   * Close all open items without saving a snapshot.
   * Used when navigating on mobile or other scenarios.
   */
  const closeAllItems = () => {
    openItems.value = new Set();
    collapsedOpenItemsSnapshot.value = new Set();
  };

  // Watch for state changes and update body classes and localStorage
  watch(
    () => ({
      isMenuOpen: menuState.value.isMenuOpen,
      isMenuCollapsed: menuState.value.isMenuCollapsed,
      isMenuHidden: menuState.value.isMenuHidden,
    }),
    () => {
      updateBodyClasses();
      // Save menu state to localStorage
      localStorage.setItem('menuCollapsed', menuState.value.isMenuCollapsed);
    },
    { deep: true },
  );

  // Initialize based on localStorage and current screen size
  const initializeMenu = () => {
    const savedMenuState = localStorage.getItem('menuCollapsed');
    const isMobile = window.innerWidth < 1200;

    if (isMobile) {
      hideMenu();
    } else if (savedMenuState === 'true') {
      collapseMenu();
    } else {
      expandMenu();
    }

    // Note: Hover events are now setup in MenuLeftLayout.vue component
    // to ensure proper lifecycle management and avoid duplicate handlers
  };

  return {
    // Menu-level state
    isMenuOpen,
    isMenuCollapsed,
    isMenuHidden,
    toggleMenu,
    openMenu,
    hideMenu,
    expandMenu,
    collapseMenu,
    initializeMenu,

    // Item-level state
    isItemOpen,
    toggleItem,
    setItemOpen,
    snapshotAndCloseAll,
    restoreFromSnapshot,
    closeAllItems,
    openItems,
  };
}
